// src/components/WhatsAppButton.jsx
// Floating WhatsApp Button für schnellen Kundenkontakt

import { useState } from "react";
import { MessageCircle, X } from "lucide-react";

const PHONE = "491634194552"; // Ohne + und Leerzeichen
const DEFAULT_MESSAGE = "Hallo Herr Tuscher, ich interessiere mich für Ihre Betonrohrfallen und hätte eine Frage.";

export default function WhatsAppButton() {
    const [expanded, setExpanded] = useState(false);
    const [message, setMessage] = useState(DEFAULT_MESSAGE);

    const whatsappUrl = `https://wa.me/${PHONE}?text=${encodeURIComponent(message)}`;

    return (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
            {/* Expanded Chat Box */}
            {expanded && (
                <div className="animate-in slide-in-from-bottom-4 fade-in duration-300 w-80 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl overflow-hidden">
                    {/* Header */}
                    <div className="bg-[#25D366] px-4 py-3 flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center">
                            <MessageCircle className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1">
                            <div className="text-white font-bold text-sm">Helmut Tuscher</div>
                            <div className="text-white/80 text-xs">Antwortet meist innerhalb 1 Stunde</div>
                        </div>
                        <button 
                            onClick={() => setExpanded(false)}
                            className="text-white/80 hover:text-white p-1"
                            aria-label="Schließen"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Chat Area */}
                    <div className="p-4 bg-[#ECE5DD] dark:bg-slate-800 min-h-[100px]">
                        <div className="bg-white dark:bg-slate-700 rounded-lg p-3 shadow-sm max-w-[85%]">
                            <p className="text-sm text-slate-700 dark:text-slate-200">
                                Hallo! 👋 Haben Sie Fragen zu meinen Betonrohrfallen? Schreiben Sie mir direkt auf WhatsApp!
                            </p>
                            <span className="text-[10px] text-slate-400 mt-1 block text-right">Helmut</span>
                        </div>
                    </div>

                    {/* Input Area */}
                    <div className="p-3 border-t border-slate-100 dark:border-slate-700">
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Ihre Nachricht..."
                                className="flex-1 rounded-full border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#25D366]/50"
                            />
                            <a
                                href={whatsappUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="h-10 w-10 rounded-full bg-[#25D366] flex items-center justify-center hover:bg-[#20BD5A] transition-colors shadow-lg"
                                aria-label="WhatsApp öffnen"
                            >
                                <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {/* Floating Button */}
            <button
                onClick={() => setExpanded(!expanded)}
                className={`group h-14 w-14 rounded-full bg-[#25D366] flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 transition-all ${expanded ? 'rotate-0' : 'animate-bounce-subtle'}`}
                aria-label="WhatsApp Chat öffnen"
            >
                {expanded ? (
                    <X className="h-6 w-6 text-white" />
                ) : (
                    <svg className="h-7 w-7 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                )}
            </button>

            {/* Tooltip (nur wenn nicht expanded) */}
            {!expanded && (
                <div className="absolute bottom-16 right-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <div className="bg-slate-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg whitespace-nowrap shadow-lg">
                        Fragen? Schreib mir! 💬
                    </div>
                </div>
            )}
        </div>
    );
}
